/**
 * SPEC-20260507-1400 (Fase 4): wrappers REST do AI Service.
 */

import { apiFetch } from "./auth";

interface ApiSuccess<T> { success: true; data: T }

export type AIProvider = "anthropic" | "openai" | "bedrock" | "codex" | "claude-code" | "gemini";

export interface AIToolsOptions {
  enabled: boolean;
  /** Habilita ask_user (sessão only). */
  askUserEnabled?: boolean;
  /** Override de quais tools incluir. Quando ausente, usa always-on. */
  available?: string[];
  /** Max iterações do loop. Default 10. */
  maxIterations?: number;
}

export interface AIOptions {
  provider?: AIProvider;
  model?: string;
  thinking?: { enabled: boolean; budgetTokens?: number };
  effort?: "low" | "medium" | "high";
  maxTokens?: number;
  tools?: AIToolsOptions;
}

export interface DeclaredPlaceholder {
  /** SPEC-2300: camelCase. */
  name: string;
  source: string;
  description: string;
  type: "string" | "number" | "boolean" | "url" | "choice" | "date" | "datetime";
  default?: string;
  /** Operator-editable override do default. SPEC-2300. */
  value?: string;
  /** Lista de choices quando type=choice. */
  choices?: string[];
}

export interface DeclaredSecret {
  /** SPEC-2300: camelCase. Primário. */
  name: string;
  /** UPPER_SNAKE — opcional, derivado de `name` quando ausente. */
  envVar?: string;
  secretPath: string;
  description: string;
  required: boolean;
}

/**
 * SPEC-20260507-2030: pergunta individual num batch ask_user.
 */
export interface PendingQuestion {
  /** ID único pra mapear resposta. Ex: 'namespace', 'instanceName'. */
  name: string;
  question: string;
  expectedType: "text" | "number" | "choice" | "secret-path";
  choices?: string[];
  default?: string;
  hint?: string;
}

export interface PendingToolCall {
  toolUseId: string;
  toolName?: string;
  /** SEMPRE array — IA pode mandar 1 ou N perguntas em batch. */
  questions: PendingQuestion[];
  /** Backwards compat (sessões antigas que persistiram single). */
  question?: string;
  expectedType?: "text" | "number" | "choice" | "secret-path";
  choices?: string[];
}

export interface ValidationResult {
  valid: boolean;
  issues: Array<{ severity: "critical" | "warn"; line?: number; message: string }>;
  suggestions: string[];
  raw: string;
  thinking?: string;
  provider: AIProvider;
  model: string;
  usage: { inputTokens: number; outputTokens: number; thinkingTokens?: number };
}

export interface GenerationResult {
  script: string;
  provider: AIProvider;
  model: string;
  usage: { inputTokens: number; outputTokens: number };
  thinking?: string;
  /** SPEC-20260507-1700: placeholders declarados pela IA. */
  placeholders?: DeclaredPlaceholder[];
  /** SPEC-20260507-1700: secrets declarados pela IA. */
  secrets?: DeclaredSecret[];
}

export interface ProviderCatalogEntry {
  provider: AIProvider;
  implemented: boolean;
  configured: boolean;
  suggestedModels: Array<{ id: string; label: string; thinking: boolean }>;
  note?: string;
}

export interface AIProvidersCatalog {
  providers: ProviderCatalogEntry[];
  default: { provider: AIProvider; model: string };
}

export async function listAIProviders(): Promise<AIProvidersCatalog> {
  const r = await apiFetch<ApiSuccess<AIProvidersCatalog>>("/api/ai/providers");
  return r.data;
}

export async function validateScript(input: {
  language: "bash" | "python" | "node" | "terraform" | "k8s";
  description: string;
  scriptExecute?: string;
  scriptRollback?: string;
  kind: "execute" | "rollback";
  options?: AIOptions;
}): Promise<ValidationResult> {
  const r = await apiFetch<ApiSuccess<ValidationResult>>("/api/ai/validate-script", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return r.data;
}

export async function generateScript(input: {
  language: "bash" | "python" | "node" | "terraform" | "k8s";
  description: string;
  scriptExecute?: string;
  kind: "execute" | "rollback";
  options?: AIOptions;
}): Promise<GenerationResult> {
  const r = await apiFetch<ApiSuccess<GenerationResult>>("/api/ai/generate-script", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return r.data;
}

// ─── Interactive script generation (SPEC-20260507-2030) ────────────────────

export interface InteractiveScriptStartInput {
  language: "bash" | "python" | "node" | "terraform" | "k8s";
  description: string;
  scriptExecute?: string;
  kind: "execute" | "rollback";
  options?: AIOptions;
  /**
   * Se passado, reusa sessão existente (caso típico: gerar rollback após execute).
   * IA recebe o histórico completo de Q&A + script anterior automaticamente.
   */
  existingSessionId?: string;
}

export interface InteractiveScriptResponse {
  sessionId: string;
  userMessage: AIChatMessageRow;
  assistantMessage: AIChatMessageRow;
  status: "ok" | "awaiting_input";
  /** Quando status=awaiting_input, IA está perguntando algo. */
  pendingToolCall?: PendingToolCall;
  /** Quando status=ok, script final pronto pra colar no editor. */
  script?: string;
  /** Placeholders/secrets declarados durante a geração (side channel). */
  declaredSideChannel?: { placeholders?: DeclaredPlaceholder[]; secrets?: DeclaredSecret[] };
}

/**
 * Inicia geração interativa: cria sessão chat + manda primeira mensagem ao IA
 * com `ask_user` habilitado. Frontend recebe `awaiting_input` quando IA quer
 * perguntar algo, ou `ok` com script final.
 */
export async function generateScriptInteractive(
  input: InteractiveScriptStartInput,
): Promise<InteractiveScriptResponse> {
  const r = await apiFetch<ApiSuccess<InteractiveScriptResponse>>(
    "/api/ai/generate-script-interactive",
    { method: "POST", body: JSON.stringify(input) },
  );
  return r.data;
}

/**
 * Continua a geração interativa respondendo a um `ask_user` pendente.
 * Quando IA pergunta de novo, retorna awaiting_input; quando termina, retorna
 * ok + script final. Reusa o endpoint existente de tool-answer.
 *
 * `answers` (preferido): Record<questionName, value> — formato batch.
 * `answer` (legacy): single string.
 */
export async function answerScriptInteractive(
  sessionId: string,
  msgId: string,
  input: { answers?: Record<string, string>; answer?: string; options?: AIOptions },
): Promise<InteractiveScriptResponse> {
  const r = await apiFetch<
    ApiSuccess<{
      resolvedMessage: AIChatMessageRow;
      nextPendingMessage: AIChatMessageRow | null;
      status: "ok" | "awaiting_input";
      pendingToolCall?: PendingToolCall;
    }>
  >(`/api/ai/sessions/${sessionId}/messages/${msgId}/tool-answer`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  // Adapta payload do tool-answer pra mesmo shape de InteractiveScriptResponse.
  // Quando status=ok, o script final está em resolvedMessage.content (sem fences).
  const isPending = r.data.status === "awaiting_input";
  const finalMessage = isPending && r.data.nextPendingMessage
    ? r.data.nextPendingMessage
    : r.data.resolvedMessage;
  const sideChannel = (finalMessage.declaredSideChannel ?? null) as InteractiveScriptResponse["declaredSideChannel"];
  return {
    sessionId,
    userMessage: r.data.resolvedMessage, // O resolvedMessage é o assistant message que tinha pendingToolCall.
    assistantMessage: finalMessage,
    status: r.data.status,
    ...(r.data.pendingToolCall ? { pendingToolCall: r.data.pendingToolCall } : {}),
    ...(!isPending ? { script: stripMarkdownFences(finalMessage.content).trim() } : {}),
    ...(sideChannel ? { declaredSideChannel: sideChannel } : {}),
  };
}

function stripMarkdownFences(content: string): string {
  const m = content.match(/^```[a-zA-Z0-9]*\s*\n([\s\S]*?)\n```\s*$/);
  if (m?.[1]) return m[1];
  return content;
}

// ─── Chat API ───────────────────────────────────────────────────────────────

export interface ChatMessage {
  role: "user" | "assistant" | "system";
  content: string;
}

export interface ChatStatelessResponse {
  content: string;
  thinking?: string;
  model: string;
  provider: AIProvider;
  usage: { inputTokens: number; outputTokens: number };
  latencyMs: number;
}

export async function chatStateless(input: {
  systemPrompt?: string;
  userPrompt?: string;
  messages?: ChatMessage[];
  options?: AIOptions;
}): Promise<ChatStatelessResponse> {
  const r = await apiFetch<ApiSuccess<ChatStatelessResponse>>("/api/ai/chat", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return r.data;
}

export interface AIChatSession {
  id: string;
  actorSub: string;
  title: string | null;
  provider: string;
  model: string;
  systemPrompt: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

export interface AIChatMessageRow {
  id: string;
  sessionId: string;
  role: "user" | "assistant" | "system";
  content: string;
  thinking?: string | null;
  provider?: string | null;
  model?: string | null;
  inputTokens?: number | null;
  outputTokens?: number | null;
  thinkingTokens?: number | null;
  latencyMs?: number | null;
  pendingToolCall?: PendingToolCall | null;
  toolAnsweredAt?: string | null;
  declaredSideChannel?: { placeholders?: DeclaredPlaceholder[]; secrets?: DeclaredSecret[] } | null;
  createdAt: string;
}

export async function createChatSession(input: {
  title?: string;
  systemPrompt?: string;
  provider?: AIProvider;
  model?: string;
  metadata?: Record<string, unknown>;
}): Promise<AIChatSession> {
  const r = await apiFetch<ApiSuccess<AIChatSession>>("/api/ai/sessions", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return r.data;
}

export async function listChatSessions(includeArchived = false): Promise<AIChatSession[]> {
  const r = await apiFetch<ApiSuccess<AIChatSession[]>>(
    `/api/ai/sessions?includeArchived=${includeArchived}`,
  );
  return r.data;
}

export async function getChatSession(id: string): Promise<{ session: AIChatSession; messages: AIChatMessageRow[] }> {
  const r = await apiFetch<ApiSuccess<{ session: AIChatSession; messages: AIChatMessageRow[] }>>(
    `/api/ai/sessions/${id}`,
  );
  return r.data;
}

export interface SendChatMessageResult {
  userMessage: AIChatMessageRow;
  assistantMessage: AIChatMessageRow;
  status: "ok" | "awaiting_input";
  pendingToolCall?: PendingToolCall;
}

export async function sendChatMessage(
  sessionId: string,
  input: { content: string; options?: AIOptions },
): Promise<SendChatMessageResult> {
  const r = await apiFetch<ApiSuccess<SendChatMessageResult>>(
    `/api/ai/sessions/${sessionId}/messages`,
    { method: "POST", body: JSON.stringify(input) },
  );
  return r.data;
}

export interface AnswerToolCallResult {
  resolvedMessage: AIChatMessageRow;
  nextPendingMessage: AIChatMessageRow | null;
  status: "ok" | "awaiting_input";
  pendingToolCall?: PendingToolCall;
}

export async function answerToolCall(
  sessionId: string,
  msgId: string,
  input: { answer: string; options?: AIOptions },
): Promise<AnswerToolCallResult> {
  const r = await apiFetch<ApiSuccess<AnswerToolCallResult>>(
    `/api/ai/sessions/${sessionId}/messages/${msgId}/tool-answer`,
    { method: "POST", body: JSON.stringify(input) },
  );
  return r.data;
}

export async function archiveChatSession(id: string): Promise<AIChatSession> {
  const r = await apiFetch<ApiSuccess<AIChatSession>>(`/api/ai/sessions/${id}/archive`, {
    method: "POST",
    body: "{}",
  });
  return r.data;
}
